<template>
    <div v-if="can.view" class="h-full">

        <div v-if="fetchingJob" class="flex-grow flex items-center justify-center">
            <loading></loading>
        </div>

        <error-404 v-else-if="!job"></error-404>

        <div v-else class="h-full flex flex-col">

            <header class="flex items-center justify-between mb-32">
                <div class="inline-flex items-center">
                    <h1 class="font-semibold text-heading-2 first-letter:uppercase">
                        {{ title }}
                    </h1>

                    <div class="flex items-center gap-8 ml-16">
                        <simple-button
                            icon="edit"
                            look="icon"
                            small
                            v-if="can.manage"
                            @click="edit"
                        />

                        <job-internal-notes :can-manage="can.manage" :job="job"/>

                        <tooltip v-if="can.analytics.view" :content="$t('jobs.stats.info')" position="bottom">
                            <simple-button
                                icon="bar-chart"
                                look="icon"
                                small
                                @click="stats"
                            />
                        </tooltip>

                        <tooltip :content="$t('applications.manage_tags')" position="bottom">
                            <simple-button
                                icon="sell"
                                look="icon"
                                small
                                v-if="can.manage"
                                @click="toggleTagsManager"
                            />
                        </tooltip>
                        <simple-button
                            class="ml-8"
                            :display="isSubscribed ? 'primary' : 'neutral'"
                            :icon="isSubscribed ? 'notifications-filled' : 'notifications'"
                            icon-position="left"
                            look="text"
                            @click="toggleSubscription"
                        >
                            {{ isSubscribed ? $t('applications.turn_off_notifications') : $t('applications.turn_on_notifications') }}
                        </simple-button>
                    </div>
                </div>

                <div class="flex items-center">
                    <div class="inline-flex items-center mr-16">
                        <permission-users :target="job" class="pl-24 mr-16"/>
                        <simple-button
                            display="neutral"
                            icon="add"
                            look="icon"
                            small
                            v-if="can.strictManage"
                            @click="manageSubscriptions"
                        />
                    </div>

                    <div class="flex mr-12">
                        <div
                            class="flex items-center border rounded-4 w-fit cursor-pointer bg-white border-primary-40"
                        >
                            <a
                                class="flex items-center gap-8 py-8 px-16 hover:bg-primary-20 text-black"
                                :href="job.url[jobsiteLocale]"
                                @click.prevent="openPreview"
                            >
                                <svg-icon type="link" class="w-18 h-18" />
                                {{ $t('applications.preview_unpublished') }}
                            </a>
                            <simple-dropdown
                                v-if="activeLocale && activeLocale.length > 1"
                                class="px-4 self-stretch border-l border-primary-40"
                            >
                                <template #trigger>
                                    <button
                                        class="flex gap-4 text-body-sm my-auto"
                                    >
                                        <span class="uppercase">{{ jobsiteLocale }}</span>
                                        <svg-icon class="w-18 h-18" type="keyboard-arrow-down"></svg-icon>
                                    </button>
                                </template>
                                <template #content="{ close }">
                                    <button v-for="locale in activeLocale"
                                            class="flex gap-4 items-center hover:bg-primary px-16 py-8 w-full text-primary-80 hover:text-white text-left transition-colors"
                                            @click="setJobsiteLanguage(locale.value); close();"
                                    >
                                        <span class="uppercase">{{ locale.value }}</span>
                                        -
                                        <span class="first-letter:uppercase">{{ locale.label }}</span>
                                    </button>
                                </template>
                            </simple-dropdown>
                        </div>
                    </div>

                    <simple-button
                        icon="share"
                        look="primary"
                        @click="edit(5)"
                    >
                        {{ $t('applications.share') }}
                    </simple-button>
                </div>
            </header>

            <job-detail v-if="can.applications.view" :first-stage="firstStage" :job="job"></job-detail>
            <error-403 v-else></error-403>
        </div>

        <permission-manager
            v-if="managingSubscriptions"
            :resource-title="translated(job.title, locale, true)"
            :target="job"
            @close="stopManagingSubscribers"
        ></permission-manager>

        <share-job-popout v-if="job" :job="job" :show="showShare" @close="closeShare"/>

        <tags-manager
            v-if="managingTags"
            :title="title"
            :parent="job"
            :service="service()"
            :model="model"
            type="jobs"
            :can-manage="can.manage"
            @close="toggleTagsManager(false)"
        />
    </div>

    <error-403 v-else></error-403>
</template>

<script>
import JobInternalNotes from '../components/JobInternalNotes';
import * as jobs from '../services/JobService';
import Job from '../models/Job';
import Loading from '../components/ui/Loading.vue';
import {normalizeJson} from '../../shared/helpers';
import RoundButtonVue from '../components/ui/RoundButton.vue';
import SimpleButtonVue from '../components/ui/SimpleButton.vue';
import AddJobVue from '../components/ui/AddJob.vue';
import JobDetailVue from '../components/JobDetail.vue';
import SvgIcon from '../components/ui/SvgIcon.vue';
import Tooltip from '../components/navigation/Tooltip.vue';
import * as subscriptions from '../services/SubscriptionService';
import Subscription from '../models/Subscription';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import ApplicationEditor from '../components/ui/ApplicationEditor.vue';
import Error403 from '../components/ui/Error403.vue';
import Error404 from '../components/ui/Error404.vue';
import Input from '../components/forms/Input';
import Feature from '../components/Feature';
import PermissionManager from '../components/ui/PermissionManager';
import PermissionUsers from '../components/ui/PermissionUsers';
import * as locationService from '../services/LocationService';
import Location from '../models/Location';
import ShareJobPopout from '../components/ShareJobPopout';
import LanguageDropdown from '../components/ui/LanguageDropdown';
import moment from 'moment';
import TagsManager from "../components/TagsManager.vue";
import SimpleDropdown from "../components/ui/SimpleDropdown.vue";
import {mapStores} from "pinia";
import {useAuthenticationStore} from "../state/modules/authentication";
import {useMainStore} from "../state/store";
import {useCompaniesStore} from "../state/modules/companies";
import {useNotificationsStore} from "../state/modules/notifications";
import {useApplicationsStore} from "../state/modules/applications";
import {useRepo} from "pinia-orm";

export default {
    name: 'Job',
    components: {
        SimpleDropdown,
        LanguageDropdown,
        ShareJobPopout,
        PermissionUsers,
        PermissionManager,
        Feature,
        TagsManager,
        'simple-input': Input,
        'loading': Loading,
        'round-button': RoundButtonVue,
        'simple-button': SimpleButtonVue,
        'add-job': AddJobVue,
        'job-detail': JobDetailVue,
        'svg-icon': SvgIcon,
        'tooltip': Tooltip,
        'application-editor': ApplicationEditor,
        'error-403': Error403,
        'error-404': Error404,
        'job-internal-notes': JobInternalNotes,
    },
    props: {
        id: {
            required: true,
        },
    },
    data: function () {
        return {
            hasAccessToFeature: false,
            managingTags: false,
            fetchingJob: true,
            managingSubscriptions: false,
            forceJobsiteLanguage: null,
            showShare: false,
            subscribing: false,
        };
    },
    computed: {
        ...mapStores(
            useMainStore,
            useCompaniesStore,
            useApplicationsStore,
            useNotificationsStore,
            useAuthenticationStore
        ),
        locale() {
            return this.mainStore.locale;
        },
        currentUserId() {
            return this.authenticationStore.currentUserId;
        },
        currentCompany() {
            return this.authenticationStore.currentCompany;
        },
        currentCompanyId() {
            return this.currentCompany.id;
        },
        contentLanguage() {
            return this.authenticationStore.contentLanguage;
        },
        activeLocale() {
            let activeLocale = [];

            for (let locale in this.currentCompany.jobsite.active_languages) {
                activeLocale.push(this.currentCompany.jobsite.active_languages[locale]);
            }

            return activeLocale.map(locale => ({
                label: this.$t('languages.' + locale + '.title'),
                value: locale,
            }));
        },
        jobsiteLocale() {
            if (this.forceJobsiteLanguage) {
                return this.forceJobsiteLanguage;
            }

            if (this.currentCompany.jobsite.active_languages.includes(this.locale)) {
                return this.locale;
            }

            if(this.activeLocale.length && this.activeLocale[0]['value']){
                return this.activeLocale[0]['value'];
            }

            return this.locale;
        },
        job() {
            let job = useRepo(Job)
                .with('subscriptions', (q) => {
                    q.with('user')
                })
                .with('filters')
                .with('tags')
                .with('stages')
                .with('vdabJob')
                .with('attachments')
                .with('jobChannels')
                .where('company_id', this.currentCompanyId)
                .whereId(parseInt(this.id))
                .first();

            // avoid duplicated ids
            if (job && job.tags) job.tags = [...new Set(job.tags)]

            return job
        },
        title() {
            return this.translated(this.job.title, this.contentLanguage, true) || this.$t('jobs.missing_title');
        },
        model() {
            return useRepo(Job);
        },
        firstStage() {
            return this.job.stages.find(stage => stage.order == 0);
        },
        userSubscription() {
            return this.job.subscriptions.find(subscription => {
                return subscription.user_id === this.currentUserId;
            });
        },
        isSubscribed() {
            return !!this.userSubscription;
        },
        subscriptionToolTip() {
            return this.isSubscribed ? this.$t('applications.turn_off_notifications') : this.$t('applications.turn_on_notifications');
        },
        can() {
            return {
                view: this.$can(['jobs.view', 'jobs.view.' + this.id]),
                manage: this.$can(['jobs.manage', 'jobs.manage.' + this.id]),
                strictManage: this.$can(['jobs.manage']) || this.job?.responsible_user_id === this.currentUserId,
                applications: {
                    view: this.$can(['applications.view', 'job-applications.view.' + this.id]),
                    manage: this.$can(['applications.manage', 'job-applications.manage.' + this.id]),
                },
                analytics: {
                    view: this.$can('analytics.view')
                },
            };
        },
    },
    methods: {
        stats() {
            this.$router.push({
                name: this.hasAccessToFeature ? 'analyticsWithId' : 'jobs.stats',
                params: {
                    id: this.job.id,
                },
            });
        },
        edit(step = 1) {
            this.$router.push({
                name: 'jobs.edit',
                params: {
                    id: this.job.id,
                },
                query: {
                    step
                },
            });
        },
        toggleTagsManager(show = true) {
            this.managingTags = show;
        },
        service() {
            return jobs;
        },
        async toggleSubscription() {
            if (this.subscribing) {
                return;
            }

            this.subscribing = true;
            if (this.isSubscribed) {
                await this.unsubscribeFromJob();
            } else {
                await this.subscribeToJob();
            }
            this.subscribing = false;
        },
        subscribeToJob() {
            return subscriptions.store({
                data: {
                    attributes: {},
                    relationships: {
                        user: {
                            data: {
                                type: 'user',
                                id: this.currentUserId,
                            },
                        },
                        subable: {
                            data: {
                                type: 'jobs',
                                id: this.job.id,
                            },
                        },
                    },
                },
            }).then(result => {
                return useRepo(Subscription).save(normalizeJson(result));
            }).then(inserts => {
                this.notificationsModuleStore[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('jobs.notifications_turned_on', {job: this.translated(this.job.title, this.locale, true)}),
                });
            });
        },
        unsubscribeFromJob() {
            let subId = this.userSubscription.id;
            return subscriptions.destroy(subId).then(() => {
                useRepo(Subscription).destroy(subId);
            }).then(() => {
                this.notificationsModuleStore[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('jobs.notifications_turned_off', {job: this.translated(this.job.title, this.locale, true)}),
                });
            });
        },
        manageSubscriptions() {
            this.managingSubscriptions = true;
        },
        stopManagingSubscribers() {
            this.managingSubscriptions = false;
        },
        setJobsiteLanguage(locale) {
            this.forceJobsiteLanguage = locale;
        },
        openPreview() {
            if (!this.job.url[this.jobsiteLocale]) {
                return this.notificationsModuleStore[ADD_NOTIFICATION]({
                    type: 'error',
                    message: this.$t('jobs.preview_blocked', {lang: this.$t('languages.' + this.jobsiteLocale + '.title')}),
                });
            }

            //show with no hash
            let url = this.job.url[this.jobsiteLocale];

            //check is jobsite is published in this language.
            if (!this.job.published || !this.job.active[this.jobsiteLocale] || (this.job.schedule && moment().utc().isBefore(moment.utc(this.job.start_date)))) {
                url += '?previewHash=' + this.job.previewHash;
            }

            window.open(url, '_blank').focus();
        },
        openShare() {
            this.showShare = true;
        },
        closeShare() {
            this.showShare = false;
        },
        setAccessToFeature() {
            return new Promise(resolve => {
                this.hasAccessToFeature = !!this.companiesModuleStore.hasFeature('analytics_filter')
                resolve();
            });
        }
    },
    created() {
        //get company location for vdab
        if (this.can.strictManage) {
            locationService.index().then(result => {
                return useRepo(Location).save(normalizeJson(result.data));
            });
        }
    },
    mounted() {
        this.applicationsModuleStore.clearApplications();
        this.setAccessToFeature()
            .then(() => {
                if (this.hasAccessToNewFeature) this.fetch();
            });
        jobs.find(this.id).then(result => {
            return useRepo(Job).save(normalizeJson(result));
        }).finally(() => {
            this.fetchingJob = false;
        });
    },
};
</script>
